import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Container,
  Text,
  Grid,
  Card,
  Group,
  Table,
  Button,
  Divider,
  Paper,
  Badge,
  Modal,
  LoadingOverlay,
  Flex,
} from "@mantine/core";
import { MantineReactTable } from "mantine-react-table";
import {
  IconCalendar,
  IconReceipt,
  IconArrowRight,
  IconUser,
  IconArrowUpRight,
  IconEye,
  IconDownload,
  IconPrinter,
} from "@tabler/icons";
import AppBreadcrumb from "components/AppBreadcrumb";
import { return_api } from "../returns/return.service";
import { invoice_api } from "../invoice/invoice.service";
import { showErrorToast } from "utilities/Toast";
import { CSVLink } from "react-csv";
import { useReactToPrint } from "react-to-print";

export default function ReplacementsReport() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalExchangeValue, setTotalExchangeValue] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [currentMonthTotal, setCurrentMonthTotal] = useState(0);
  const [currentMonthRowCount, setCurrentMonthRowCount] = useState(0);
  
  // CSV Export & Print States
  const [csvData, setCsvData] = useState([]);
  const [isCsvDownloading, setIsCsvDownloading] = useState(false);
  const [printData, setPrintData] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);

  const csvRef = useRef();
  const printRef = useRef();

  // Modal States
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedOriginalInvoice, setSelectedOriginalInvoice] = useState(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let original_bill = "";
      let replacement_bill = "";
      let customer_name = "";
      let operator_name = "";

      columnFilters.forEach(filter => {
        if (filter.id === "original_invoice.invoice_id") {
          original_bill = filter.value;
        } else if (filter.id === "new_invoice.invoice_id") {
          replacement_bill = filter.value;
        } else if (filter.id === "customer_name") {
          customer_name = filter.value;
        } else if (filter.id === "operator_name") {
          operator_name = filter.value;
        }
      });

      const response = await return_api.get_replacements_report(
        startDate, 
        endDate,
        pagination.pageIndex + 1,
        pagination.pageSize,
        globalFilter || "",
        original_bill,
        replacement_bill,
        customer_name,
        operator_name
      );
      if (response && response.success) {
        setReportData(response.data || []);
        setRowCount(response.count || 0);
        setTotalExchangeValue(response.total_exchange_value || 0);

        // Fetch current month specific data for the summary card on initial/empty load
        if (!startDate && !endDate) {
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

          const formatDt = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const date = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${date}`;
          };

          const currentMonthStart = formatDt(startOfMonth);
          const currentMonthEnd = formatDt(endOfMonth);

          try {
            const currentMonthRes = await return_api.get_replacements_report(
              currentMonthStart,
              currentMonthEnd,
              1,
              1, // only need aggregate values
              "", "", "", "", "",
              true // bypass slicing
            );
            if (currentMonthRes && currentMonthRes.success) {
              setCurrentMonthTotal(currentMonthRes.total_exchange_value || 0);
              setCurrentMonthRowCount(currentMonthRes.count || 0);
            }
          } catch (cardErr) {
            console.error("Failed to fetch current month card totals:", cardErr);
          }
        }
      } else {
        showErrorToast({
          title: "Error",
          message: response?.message || "Failed to load report data.",
        });
      }
    } catch (error) {
      showErrorToast({ title: "Error", message: "An unexpected error occurred." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }));
    if (pagination.pageIndex === 0) {
      fetchReport();
    }
  }, [startDate, endDate, columnFilters, globalFilter]);

  // CSV Export Action
  const downloadCsv = async () => {
    setIsCsvDownloading(true);
    try {
      let original_bill = "";
      let replacement_bill = "";
      let customer_name = "";
      let operator_name = "";

      columnFilters.forEach(filter => {
        if (filter.id === "original_invoice.invoice_id") {
          original_bill = filter.value;
        } else if (filter.id === "new_invoice.invoice_id") {
          replacement_bill = filter.value;
        } else if (filter.id === "customer_name") {
          customer_name = filter.value;
        } else if (filter.id === "operator_name") {
          operator_name = filter.value;
        }
      });

      const response = await return_api.get_replacements_report(
        startDate, 
        endDate,
        1,
        999999, // fetch all
        globalFilter || "",
        original_bill,
        replacement_bill,
        customer_name,
        operator_name,
        true // for_csv=true to bypass slicing
      );

      if (response && response.success) {
        const rows = response.data || [];
        
        // Construct styled HTML/XML Excel document with precise column widths and gridlines
        const excelContent = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <!--[if gte mso 9]>
            <xml>
              <x:ExcelWorkbook>
                <x:ExcelWorksheets>
                  <x:ExcelWorksheet>
                    <x:Name>Replacements Report</x:Name>
                    <x:WorksheetOptions>
                      <x:DisplayGridlines/>
                    </x:WorksheetOptions>
                  </x:ExcelWorksheet>
                </x:ExcelWorksheets>
              </x:ExcelWorkbook>
            </xml>
            <![endif]-->
            <meta charset="utf-8">
            <style>
              th { background-color: #4F46E5; color: #ffffff; font-weight: bold; text-align: center; border: 1px solid #CBD5E0; font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; }
              td { border: 1px solid #CBD5E0; font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; }
            </style>
          </head>
          <body>
            <table>
              <colgroup>
                <col width="170" />
                <col width="130" />
                <col width="130" />
                <col width="130" />
                <col width="145" />
                <col width="165" />
                <col width="145" />
                <col width="145" />
                <col width="145" />
                <col width="130" />
              </colgroup>
              <thead>
                <tr>
                  <th style="height: 30px;">Exchange Date</th>
                  <th>Customer</th>
                  <th>Original Bill</th>
                  <th>OG Counter</th>
                  <th>OG Bill Total Amt</th>
                  <th>OG Bill Rplment Amt</th>
                  <th>Replacement Bill</th>
                  <th>Rplment Counter</th>
                  <th>Rplment Bill Amt</th>
                  <th>Billed Operator</th>
                </tr>
              </thead>
              <tbody>
                ${rows.map(r => {
                  const dateStr = new Date(r.created_at).toLocaleDateString("en-GB", {
                    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                  });
                  return `
                    <tr>
                      <td style="height: 25px; text-align: center;">${dateStr}</td>
                      <td>${r.customer_name || "Walk-in"}</td>
                      <td style="text-align: center; font-weight: bold;">${r.original_invoice?.invoice_id || "N/A"}</td>
                      <td>${r.original_invoice?.counter_name || "Warehouse"}</td>
                      <td style="text-align: right;">₹ ${r.original_invoice?.total ? Number(r.original_invoice.total).toFixed(2) : "0.00"}</td>
                      <td style="text-align: right; color: #DC2626;">₹ ${r.return_amount ? Number(r.return_amount).toFixed(2) : "0.00"}</td>
                      <td style="text-align: center; font-weight: bold;">${r.new_invoice?.invoice_id || "N/A"}</td>
                      <td>${r.new_invoice?.counter_name || "Warehouse"}</td>
                      <td style="text-align: right; color: #16A34A;">₹ ${r.new_invoice?.total ? Number(r.new_invoice.total).toFixed(2) : "0.00"}</td>
                      <td>${r.operator_name || "System"}</td>
                    </tr>
                  `;
                }).join("")}
              </tbody>
            </table>
          </body>
          </html>
        `;

        // Create Blob and download it with .xls extension
        const blob = new Blob([excelContent], { type: "application/vnd.ms-excel;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const dateSuffix = new Date().toLocaleDateString("en-GB").replace(/\//g, "_");
        link.href = url;
        link.setAttribute("download", `REPLACEMENTS_REPORT_${dateSuffix}.xls`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        showErrorToast({ title: "Export Error", message: "Failed to fetch export data." });
      }
    } catch (err) {
      showErrorToast({ title: "Export Error", message: err.message || "An error occurred during export." });
    } finally {
      setIsCsvDownloading(false);
    }
  };

  // Print Action
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const handlePrintAll = async () => {
    setIsPrinting(true);
    try {
      let original_bill = "";
      let replacement_bill = "";
      let customer_name = "";
      let operator_name = "";

      columnFilters.forEach(filter => {
        if (filter.id === "original_invoice.invoice_id") {
          original_bill = filter.value;
        } else if (filter.id === "new_invoice.invoice_id") {
          replacement_bill = filter.value;
        } else if (filter.id === "customer_name") {
          customer_name = filter.value;
        } else if (filter.id === "operator_name") {
          operator_name = filter.value;
        }
      });

      const response = await return_api.get_replacements_report(
        startDate, 
        endDate,
        1,
        999999, // fetch all
        globalFilter || "",
        original_bill,
        replacement_bill,
        customer_name,
        operator_name,
        true // for_csv=true to bypass slicing
      );

      if (response && response.success) {
        setPrintData(response.data || []);
        setTimeout(() => {
          handlePrint();
        }, 200);
      } else {
        showErrorToast({ title: "Print Error", message: "Failed to fetch print data." });
      }
    } catch (err) {
      showErrorToast({ title: "Print Error", message: err.message || "An error occurred during print request." });
    } finally {
      setIsPrinting(false);
    }
  };

  const PrintComponent = () => (
    <div ref={printRef} style={{ padding: 25, fontFamily: "sans-serif" }}>
      <h2 style={{ textAlign: "center", marginBottom: 5, color: "#2B2D42" }}>Replacement & Exchange Bills Report</h2>
      {startDate || endDate ? (
        <p style={{ textAlign: "center", margin: "0 0 15px 0", fontSize: 13, color: "#555" }}>
          Date Range: {startDate ? startDate : "Beginning"} to {endDate ? endDate : "End"}
        </p>
      ) : null}
      <table border="1" style={{ width: "100%", borderCollapse: "collapse", marginTop: 15, fontSize: 12 }} cellPadding="6">
        <thead>
          <tr style={{ background: "#f1f3f5" }}>
            <th>Exchange Date</th>
            <th>Customer</th>
            <th>Original Bill</th>
            <th>OG Counter</th>
            <th>OG Bill Total Amt</th>
            <th>OG Bill Rplment Amt</th>
            <th>Replacement Bill</th>
            <th>Rplment Counter</th>
            <th>Rplment Bill Amt</th>
            <th>Billed Operator</th>
          </tr>
        </thead>
        <tbody>
          {printData?.map((r, i) => (
            <tr key={i}>
              <td>
                {new Date(r.created_at).toLocaleDateString("en-GB", {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                })}
              </td>
              <td>{r.customer_name || "Walk-in"}</td>
              <td style={{ textAlign: "center", fontWeight: "bold" }}>{r.original_invoice?.invoice_id || "N/A"}</td>
              <td>{r.original_invoice?.counter_name || "Warehouse"}</td>
              <td style={{ textAlign: "right" }}>₹ {Number(r.original_invoice?.total || 0).toFixed(2)}</td>
              <td style={{ textAlign: "right", color: "red" }}>₹ {Number(r.return_amount || 0).toFixed(2)}</td>
              <td style={{ textAlign: "center", fontWeight: "bold" }}>{r.new_invoice?.invoice_id || "N/A"}</td>
              <td>{r.new_invoice?.counter_name || "Warehouse"}</td>
              <td style={{ textAlign: "right", color: "green" }}>₹ {Number(r.new_invoice?.total || 0).toFixed(2)}</td>
              <td>{r.operator_name || "System"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const handleViewInvoice = async (item) => {
    setModalLoading(true);
    setModalOpened(true);
    setSelectedOriginalInvoice(item.original_invoice);
    try {
      const res = await invoice_api.get_invoice_by_id(item.new_invoice.id);
      if (res && res.data && res.data.success) {
        setSelectedInvoice(res.data.data);
      } else {
        showErrorToast({ title: "Error", message: "Failed to load invoice details." });
      }
    } catch (err) {
      showErrorToast({ title: "Error", message: "Failed to load invoice details." });
    } finally {
      setModalLoading(false);
    }
  };

  const isDecimalUnit = (unit) => {
    if (!unit) return false;
    const decimalUnits = ['kgs','gm','kg','gram','ltr','ml','mtr'];
    return decimalUnits.includes(unit.toLowerCase());
  };

  // Calculate Modal details
  const modalItems = selectedInvoice?.invoice_item || [];
  const modalTotalItems = modalItems.length;
  
  let modalTotalPcs = 0;
  let modalTotalKgs = 0;
  let modalTotalAmount = selectedInvoice?.total || 0;

  modalItems.forEach(item => {
    const qty = parseFloat(item.qty) || 0;
    const unit = item.units || item.unit;
    if (isDecimalUnit(unit)) {
      modalTotalKgs += qty;
    } else {
      modalTotalPcs += qty;
    }
  });

  const columns = useMemo(() => [
    {
      header: "Exchange Date",
      accessorKey: "created_at",
      size: 130,
      Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
      }),
    },
    {
      header: "Customer",
      accessorKey: "customer_name",
      size: 100,
      Cell: ({ cell }) => (
        <Group spacing="xs">
          <IconUser size={14} color="#718096" />
          <Text fw={500}>{cell.getValue()}</Text>
        </Group>
      ),
    },
    {
      header: "Original Bill",
      accessorKey: "original_invoice.invoice_id",
      size: 110,
      Cell: ({ row }) => {
        const orig = row.original.original_invoice;
        return (
          <Box>
            <Badge color="red" variant="outline">{orig.invoice_id}</Badge>
            {orig.counter_name && (
              <Text size="xs" color="dimmed" mt={4}>
                {orig.counter_name}
              </Text>
            )}
          </Box>
        );
      },
    },
    {
      header: "OG Bill Total Amt",
      accessorKey: "original_invoice.total",
      size: 120,
      Cell: ({ cell }) => <Text fw={600}>₹ {Number(cell.getValue()).toFixed(2)}</Text>,
    },
    {
      header: "OG Bill Rplment Amt",
      accessorKey: "return_amount",
      size: 130,
      Cell: ({ cell }) => <Text fw={600} color="red">₹ {Number(cell.getValue() || 0).toFixed(2)}</Text>,
    },
    {
      header: "Replacement Bill",
      accessorKey: "new_invoice.invoice_id",
      size: 120,
      Cell: ({ row }) => {
        const newInv = row.original.new_invoice;
        return (
          <Box>
            <Badge color="green" variant="filled">{newInv.invoice_id}</Badge>
            {newInv.counter_name && (
              <Text size="xs" color="dimmed" mt={4}>
                {newInv.counter_name}
              </Text>
            )}
          </Box>
        );
      },
    },
    {
      header: "Rplment Bill Amt",
      accessorKey: "new_invoice.total",
      size: 120,
      Cell: ({ cell }) => <Text fw={600} color="teal">₹ {Number(cell.getValue()).toFixed(2)}</Text>,
    },
    {
      header: "Billed Operator",
      accessorKey: "operator_name",
      size: 120,
      Cell: ({ cell }) => <Text size="sm">{cell.getValue()}</Text>,
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => (
        <Button
          variant="subtle"
          color="blue"
          size="xs"
          onClick={() => handleViewInvoice(row.original)}
        >
          <IconEye size={18} />
        </Button>
      ),
    },
  ], []);

  return (
    <>
      <CSVLink
        style={{ display: "none" }}
        data={csvData}
        filename={`REPLACEMENTS_REPORT_${new Date().toLocaleDateString("en-GB").replace(/\//g, "_")}.csv`}
        ref={csvRef}
      />

      <div style={{ display: "none" }}>
        <PrintComponent />
      </div>

      <Box px={32} py={24} style={{ minHeight: "90vh" }}>
        <Container fluid p={0}>
          <Box mb={24}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
              <Box>
                <Text size="xl" fw={700} style={{ color: "#2B2D42", letterSpacing: "0.5px" }}>
                  Replacement & Exchange Bills Report
                </Text>
                <AppBreadcrumb
                  crumbs={[
                    { text: "Reports" },
                    { text: "Replacements Report" },
                  ]}
                />
              </Box>
              <Flex gap={12}>
                <Button
                  onClick={downloadCsv}
                  leftIcon={<IconDownload size={16} />}
                  loading={isCsvDownloading}
                  variant="light"
                  color="blue"
                >
                  CSV Export
                </Button>
                <Button
                  onClick={handlePrintAll}
                  leftIcon={<IconPrinter size={16} />}
                  loading={isPrinting}
                  variant="light"
                  color="blue"
                >
                  Print
                </Button>
              </Flex>
            </Flex>
          </Box>

        {/* Date Filter & Stats Grid */}
        <Grid gutter="md" mb={24}>
          <Grid.Col md={8}>
            <Paper
              shadow="xs"
              p="md"
              radius="md"
              style={{
                border: "1px solid rgba(226, 232, 240, 0.8)",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <Flex justify="space-between" align="center" wrap="wrap" gap="xs" mb="sm">
                <Flex align="center" gap="xs">
                  <IconCalendar size={18} color="#4F46E5" />
                  <Text fw={600} size="sm" color="#2D3748">
                    Filter by Date Range
                  </Text>
                </Flex>
                
                {(startDate || endDate || globalFilter || columnFilters.length > 0) && (
                  <Button
                    variant="light"
                    color="red"
                    size="xs"
                    compact
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                      setGlobalFilter("");
                      setColumnFilters([]);
                    }}
                    style={{ fontSize: "11px", fontWeight: 700 }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Flex>
              
              <Flex align="center" gap="xs">
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #CBD5E0",
                    fontSize: "13px",
                    color: "#2D3748",
                    outline: "none",
                  }}
                />
                <Text size="xs" fw={700} color="dimmed" px={4}>TO</Text>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #CBD5E0",
                    fontSize: "13px",
                    color: "#2D3748",
                    outline: "none",
                  }}
                />
              </Flex>
            </Paper>
          </Grid.Col>

          <Grid.Col md={4}>
            <Paper
              shadow="xs"
              p="md"
              radius="md"
              style={{
                background: !startDate && !endDate 
                  ? "linear-gradient(135deg, #059669 0%, #10B981 100%)" 
                  : "linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)",
                color: "#ffffff",
                height: "100%",
                transition: "all 0.3s ease",
              }}
            >
              <Group position="apart">
                <Text size="xs" fw={600} style={{ opacity: 0.9 }}>
                  TOTAL EXCHANGE VALUE GENERATED
                </Text>
                {!startDate && !endDate ? (
                  <Badge 
                    variant="white" 
                    color="teal" 
                    size="xs" 
                    fw={700}
                    style={{ letterSpacing: '0.5px' }}
                  >
                    CURRENT MONTH
                  </Badge>
                ) : (
                  <Badge 
                    variant="white" 
                    color="indigo" 
                    size="xs" 
                    fw={700}
                    style={{ letterSpacing: '0.5px' }}
                  >
                    FILTERED
                  </Badge>
                )}
              </Group>
              <Text size="xl" fw={800} mt="xs">
                ₹ {(!startDate && !endDate ? currentMonthTotal : totalExchangeValue).toFixed(2)}
              </Text>
              {!startDate && !endDate ? (
                <Text size="xs" mt="xs" style={{ opacity: 0.9, fontWeight: 500 }}>
                  Across {currentMonthRowCount} replacement invoices in {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Text>
              ) : (
                <Text size="xs" mt="xs" style={{ opacity: 0.9 }}>
                  Across {rowCount} replacement invoices in selected period
                </Text>
              )}
            </Paper>
          </Grid.Col>
        </Grid>

          <MantineReactTable
            columns={columns}
            data={reportData}
            isLoading={loading}
            state={{ isLoading: loading, pagination, columnFilters, globalFilter }}
            manualPagination
            manualFiltering
            rowCount={rowCount}
            onPaginationChange={setPagination}
            onColumnFiltersChange={setColumnFilters}
            onGlobalFilterChange={setGlobalFilter}
            mantineTableProps={{ className: "datatable", striped: true }}
            mantineTableContainerProps={{ sx: { overflowX: "auto" } }}
            initialState={{ density: "xs" }}
            layoutMode="grid"
            enableColumnResizing
            columnResizeMode="onChange"
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={false}
            enableColumnFilters={true}
            enableGlobalFilter={true}
            enableStickyHeader
          />
      </Container>

      {/* Invoice Details Breakdown Modal */}
      <Modal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setSelectedInvoice(null);
        }}
        title={
          <Text fw={700} size="lg" color="#2B2D42">
            Invoice Details Breakdown
          </Text>
        }
        size="75%"
        radius="md"
        padding="xl"
        overlayOpacity={0.65}
        overlayBlur={3}
      >
        <div style={{ position: 'relative', minHeight: '200px' }}>
          <LoadingOverlay visible={modalLoading} overlayBlur={2} />
          
          {selectedInvoice && (
            <Box>
              {/* Header Summary Row */}
              <Paper p="md" mb="md" radius="sm" withBorder style={{ backgroundColor: "#F8F9FA" }}>
                <Grid gutter="xs" align="center">
                  <Grid.Col span={3}>
                    <Text size="xs" color="dimmed" fw={500}>TOTAL ITEMS</Text>
                    <Text fw={700} size="md" color="#1A202C">{modalTotalItems}</Text>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Text size="xs" color="dimmed" fw={500}>TOTAL QUANTITY</Text>
                    <Text fw={700} size="md" color="#1A202C">
                      {modalTotalPcs} pcks / {modalTotalKgs.toFixed(2)} Kg
                    </Text>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Text size="xs" color="dimmed" fw={500}>INVOICE NO</Text>
                    <Text fw={700} size="md" color="#4F46E5">{selectedInvoice.invoice_id || "N/A"}</Text>
                  </Grid.Col>
                  <Grid.Col span={3}>
                    <Text size="xs" color="dimmed" fw={500}>TOTAL AMOUNT</Text>
                    <Text fw={700} size="md" color="teal">₹ {Number(modalTotalAmount).toFixed(2)}</Text>
                  </Grid.Col>
                </Grid>
              </Paper>

              {/* Customer, Date & Counter Details */}
              <Grid mb="lg">
                <Grid.Col md={3}>
                  <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: "#FAFAFA" }}>
                    <Text size="xs" color="dimmed" fw={500}>CUSTOMER (F1)</Text>
                    <Text fw={600} size="sm" color="#2D3748" mt={4}>
                      {selectedInvoice.user_name || selectedInvoice.customer || "Walk-in Customer"}
                    </Text>
                  </Paper>
                </Grid.Col>
                <Grid.Col md={3}>
                  <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: "#FAFAFA" }}>
                    <Text size="xs" color="dimmed" fw={500}>INVOICE DATE</Text>
                    <Text fw={600} size="sm" color="#2D3748" mt={4}>
                      {new Date(selectedInvoice.create_at).toLocaleDateString("en-GB", {
                        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit"
                      })}
                    </Text>
                  </Paper>
                </Grid.Col>
                {selectedOriginalInvoice && (
                  <Grid.Col md={3}>
                    <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: "#FAFAFA" }}>
                      <Text size="xs" color="dimmed" fw={500}>ORIGINAL COUNTER</Text>
                      <Text fw={600} size="sm" color="red" mt={4}>
                        {selectedOriginalInvoice.counter_name || "N/A"}
                      </Text>
                    </Paper>
                  </Grid.Col>
                )}
                <Grid.Col md={3}>
                  <Paper p="xs" radius="sm" withBorder style={{ backgroundColor: "#FAFAFA" }}>
                    <Text size="xs" color="dimmed" fw={500}>REPLACEMENT COUNTER</Text>
                    <Text fw={600} size="sm" color="green" mt={4}>
                      {selectedInvoice.counter_name || "N/A"}
                    </Text>
                  </Paper>
                </Grid.Col>
              </Grid>

              {/* Items breakdown Table */}
              <Table withColumnBorders withBorder verticalSpacing="xs" horizontalSpacing="sm" striped highlightOnHover>
                <thead style={{ backgroundColor: "#E6F4EA" }}>
                  <tr>
                    <th style={{ width: "5%" }}>S.No</th>
                    <th style={{ width: "40%" }}>Product Item</th>
                    <th>Batch</th>
                    <th>Rate</th>
                    <th>Qty</th>
                    <th>Tax</th>
                    <th>Tax Amount</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {modalItems.map((item, idx) => (
                    <tr key={item.id}>
                      <td>{idx + 1}</td>
                      <td>
                        <Text fw={600} size="sm" color="#1A202C">{item.title}</Text>
                      </td>
                      <td>
                        {item.batch_no && (
                          <Badge color="gray" variant="outline" size="sm">
                            {item.batch_no}
                          </Badge>
                        )}
                      </td>
                      <td>₹ {Number(item.rate).toFixed(2)}</td>
                      <td>
                        <Text fw={700} color={Number(item.qty) < 0 ? "red" : "#2D3748"}>
                          {item.qty}
                        </Text>
                      </td>
                      <td>{item.tax} %</td>
                      <td>
                        <Text color={Number(item.tax_amount) < 0 ? "red" : "#2D3748"}>
                          ₹ {Number(item.tax_amount).toFixed(2)}
                        </Text>
                      </td>
                      <td>
                        <Text fw={600} color={Number(item.amount) < 0 ? "red" : "teal"}>
                          ₹ {Number(item.amount).toFixed(2)}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Footer Close Button */}
              <Group position="right" mt="xl">
                <Button variant="default" onClick={() => setModalOpened(false)}>
                  Close
                </Button>
              </Group>
            </Box>
          )}
        </div>
      </Modal>
      </Box>
    </>
  );
}
